import { COMMUNICATION_RESULT_STATUSES, TYRE_POSITIONS } from "models";
import { generateDMSNr } from "utils/appointmentUtils";
import { getDisplayedPrice, getExtensionFromURL, getNameFromURL } from "utils/common";

import type {
  Appointment,
  DealerLocation,
  InterventionAttachment,
  InterventionID,
  QuestionResult,
  QuestionResultImage,
  QuestionResultVideo,
  Snooze,
  VehicleTyre
} from "models";
import type { SeasonOption } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/hooks/useTyreTitleAndSeasons";
import type { AddInterventionPayload, OrderedGroupedCheck } from "modules/AppointmentDetails/hooks";

const toAttachment = (media: QuestionResultImage | QuestionResultVideo, type: string): InterventionAttachment => ({
  created_on: media.created_on,
  updated_on: media.updated_on,
  url: media.url,
  id: -1,
  type,
  name: getNameFromURL(media.url),
  visible_to_customer: media.visible_to_customer,
  intervention_id: 0,
  user_id: 0,
  is_final_car_check: false
});

export const parseQuestionResultAttachments = (questionResult: QuestionResult): InterventionAttachment[] => {
  return (questionResult.images ?? [])
    .filter(i => i.active)
    .map(i => toAttachment(i, `image/${getExtensionFromURL(i.url)}`))
    .concat(...(questionResult.videos ?? []).filter(v => v.active).map(v => toAttachment(v, `video/${getExtensionFromURL(v.url)}`)));
};

export const getAllSnoozeAttachments = (statusHistory: Snooze[]) => {
  if (!statusHistory.length) return [];

  return statusHistory.flatMap(({ note_attachments }) =>
    note_attachments ? note_attachments.map(url => ({ url, name: getNameFromURL(url), type: getExtensionFromURL(url) })) : []
  );
};

export const isOnCarTyre = (position: TYRE_POSITIONS): boolean => {
  return position >= TYRE_POSITIONS.CarFirst && position <= TYRE_POSITIONS.CarLast;
};

export const isInStorageTyre = (position: TYRE_POSITIONS): boolean => {
  return position >= TYRE_POSITIONS.StorageFirst && position <= TYRE_POSITIONS.StorageLast;
};

export const isOnTruckTyre = (position: TYRE_POSITIONS): boolean => {
  return position >= TYRE_POSITIONS.OnTruckFirst;
};

export const detachTyreReplacementsFromIntervention = (interventionId: InterventionID, tyres?: VehicleTyre[]) =>
  tyres?.map(tyre => {
    if (!tyre.replacements?.some(r => r.intervention_id === interventionId)) return tyre;

    return {
      ...tyre,
      replacements: tyre.replacements.map(r => (r.intervention_id === interventionId ? { ...r, intervention_id: null } : r))
    };
  });

export const isTyreStatusVisibleOnWo = (location?: DealerLocation, checks?: OrderedGroupedCheck[]) => {
  if (location?.is_tyre_status_always_visible_on_wo) return true;

  return checks?.some(check => check._isTyreCheck) ?? false;
};

export const convertQuestionResultToIntervention = ({
  title,
  questionResult,
  appointment,
  location,
  showPriceInVat,
  snoozeItem
}: {
  title: string;
  questionResult: QuestionResult;
  appointment: Appointment;
  seasonOptions: SeasonOption[];
  location?: DealerLocation;
  showPriceInVat?: boolean;
  snoozeItem?: Snooze;
}): AddInterventionPayload => {
  const questionDiagnoseOverviewStatus = appointment.customer_communication?.diagnose_overview_results?.find(r => r.question_result_id === questionResult.id)?.status;

  return {
    dms_nr: generateDMSNr(),
    title,
    customer_ok: COMMUNICATION_RESULT_STATUSES.OkStatus === questionDiagnoseOverviewStatus,
    description: questionResult.mechanic_notes || questionResult.raw || questionResult.note || "",
    price: getDisplayedPrice(questionResult.price, location, showPriceInVat),
    appointment_id: appointment.id,
    question_result_id: snoozeItem ? undefined : questionResult.id,
    question_result_status: questionResult.status,
    attachments: parseQuestionResultAttachments(questionResult),
    snooze_id: snoozeItem?.id
  };
};

export function getAttachmentIcon(url: string) {
  if (!url) return "file";

  if (url.match(/\.(jpeg|jpg|gif|png)/i)) return "image";
  if (url.match(/\.(mp4|mov|mpeg|mkv|webm)/i)) return "video";

  return "file";
}
